import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Target, Eye, Heart, Trophy, ChevronLeft, ChevronRight } from "lucide-react";
import { useRef } from "react";
export default function AboutUs() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.offsetWidth;
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth"
      });
    }
  };
  const features = [{
    icon: <Target className="size-6 text-white" />,
    title: "Mission",
    description: "Empower global logistics with intelligent sorting."
  }, {
    icon: <Eye className="size-6 text-white" />,
    title: "Vision",
    description: "Become the world's most trusted sorting brand."
  }, {
    icon: <Heart className="size-6 text-white" />,
    title: "Values",
    description: "Customer first. Quality always. Innovation forever."
  }, {
    icon: <Trophy className="size-6 text-white" />,
    title: "History",
    description: "15+ years of dedicated R&D in sorting technology."
  }];
  const timeline = [{
    year: "2009",
    event: "Huayuan Shenzhen Intelligent Equipment Co., Ltd founded in Shenzhen"
  }, {
    year: "2014",
    event: "Launched first cross-belt sorter"
  }, {
    year: "2018",
    event: "Expanded to 20+ countries"
  }, {
    year: "2022",
    event: "Achieved 500+ deployments worldwide"
  }, {
    year: "2025",
    event: "Released next-gen AI sorting platform"
  }];
  const certifications = [{
    src: "https://hysorter.com/wp-content/uploads/2026/05/微信图片_20260509231530_779_15-1.png?v=bncie2vzcp",
    alt: "ISO Certification"
  }, {
    src: "https://hysorter.com/wp-content/uploads/2026/05/微信图片_20260509231530_780_15-1.png?v=ocyeg9bzmfa",
    alt: "CE Marking"
  }, {
    src: "https://hysorter.com/wp-content/uploads/2026/05/微信图片_20260509231531_781_15-3.png?v=mqoaoxht4cj",
    alt: "Technology Patent"
  }];
  return <section id="about-us" className="bg-background py-20 md:py-32 light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="w-full">
          <div className="relative rounded-lg overflow-hidden bg-cover bg-center" role="img" aria-label="Company headquarters">
            <img src="https://exotic-skylark.10web.cloud/wp-content/uploads/2026/05/008-3.jpg" data-wvc-srcset="https://exotic-skylark.10web.cloud/wp-content/uploads/2026/05/008-3.jpg-wvc-srcset" data-wvc-sizes="https://exotic-skylark.10web.cloud/wp-content/uploads/2026/05/008-3.jpg-wvc-sizes" alt="Company headquarters" className="w-full h-full object-cover" loading="lazy" />
          </div>
        </div>

        

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-24 items-center">
          <div>
            <h2 className="text-3xl font-bold text-foreground mb-6 font-default">
              Our Certifications & Awards
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Our commitment to quality and safety is backed by international standards and industry recognition. We maintain rigorous compliance protocols to ensure every solution we deploy meets the highest global benchmarks for performance and reliability.
            </p>
          </div>
          
          <div className="relative h-[400px] bg-muted/10 rounded-lg border border-border overflow-hidden group">
            <Button variant="outline" size="icon" className="absolute left-4 top-1/2 -translate-y-1/2 z-10 rounded-lg bg-background/80 backdrop-blur-sm shadow-md opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => scroll("left")} aria-label="Scroll left">
              <ChevronLeft className="size-5" />
            </Button>

            <div ref={scrollContainerRef} className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-smooth w-full h-full">
              {certifications.map((cert, i) => <div key={i} data-index={i} className="w-full aspect-[2/3] flex-shrink-0 snap-center">
                  <img src={cert.src} data-wvc-srcset={`${cert.src}-wvc-srcset`} data-wvc-sizes={`${cert.src}-wvc-sizes`} alt={cert.alt} className="w-full h-full object-contain transition-all duration-300" loading="lazy" />
                </div>)}
            </div>

            <Button variant="outline" size="icon" className="absolute right-4 top-1/2 -translate-y-1/2 z-10 rounded-lg bg-background/80 backdrop-blur-sm shadow-md opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => scroll("right")} aria-label="Scroll right">
              <ChevronRight className="size-5" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-24">
          {features.map((feature, i) => <div key={i} data-index={i} className="bg-card p-8 rounded-lg border border-border shadow-sm hover:shadow-md transition-shadow">
              <div className="bg-chart-1 w-12 h-12 rounded-lg flex items-center justify-center mb-6">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-foreground mb-3 font-default">
                {feature.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {feature.description}
              </p>
            </div>)}
        </div>

        <div className="max-w-3xl mx-auto mb-24">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-foreground mb-16 font-default">
            History
          </h2>
          
          <div className="relative pl-8 md:pl-0">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-border md:-translate-x-1/2" />

            <div className="space-y-12">
              {timeline.map((item, i) => <div key={i} data-index={i} className="relative flex flex-col md:flex-row items-start md:items-center">
                  <div className="absolute left-[-20px] md:left-1/2 md:-translate-x-1/2 w-10 h-10 bg-chart-1 rounded-lg border-4 border-background z-10 flex items-center justify-center shadow-lg shadow-chart-1/20" />
                  
                  <div className={`w-full md:w-1/2 ${i % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16 md:ml-auto'}`}>
                    <div className="pt-1 md:pt-0">
                      <span className="text-2xl font-bold text-chart-1 block mb-1">
                        {item.year}
                      </span>
                      <p className="text-lg text-foreground font-medium">
                        {item.event}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        <div className="mt-24 text-center">
          <Button asChild className="bg-chart-1 text-white hover:bg-chart-1/90 shadow-lg shadow-chart-1/30 rounded-md px-10 py-6 h-auto text-lg font-semibold transition-all">
            <Link to="/AboutPage">Learn More About Our Journey</Link>
          </Button>
        </div>
      </div>
      <style dangerouslySetInnerHTML={{
      __html: `
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `
    }} />
    </section>;
}